import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ColorPicker from 'common/components/Elements/ColorPicker/ColorPicker';
import type { Color } from 'common/utilities/colors/colors.types';
import theme from 'core/utilities/theme';
import React from 'react';
import type { SequenceFeature,SequenceType } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import type { HandleResidueHover } from 'sequenceAnalyser/components/Sequences/SequenceTable/SequenceTable';
import type { ColumnsLetterOccurrences,LetterOccurrence } from 'sequenceAnalyser/utilities/colorCalculations/colorCalculations';
import type {SelectedColorScheme } from 'sequenceAnalyser/utilities/colors';
import { getMolecules } from 'sequenceAnalyser/utilities/colors';
import type { Residue } from 'sequenceAnalyser/utilities/sequenceAnalyser.types';
import { buildFeatureId } from 'sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser';

import classes from './SequenceResidueHoverTooltip.style';

interface ResidueTitleProps {
  residue: Residue;
  color?: Color;
  sessionSequenceType: SequenceType;
}

function ResidueTitle({ residue, color, sessionSequenceType }: ResidueTitleProps) {
  const aminoAcid = getMolecules(sessionSequenceType).find((a) => a.residue === residue.letter);
  let name = aminoAcid ? aminoAcid.name : residue.letter;
  if (name === '.') name = 'Gap';

  return (
    <Box textAlign="center" mb={1}>
      {color && <ColorPicker color={color} />}
      <Typography display="inline">{name}</Typography>
    </Box>
  );
}

function Occurrence({
  occurrence: { count, percentage },
}: { occurrence: LetterOccurrence }) {
  return (
    <Typography variant="body2">
      {`Occurrence: ${percentage}% (${count})`}
    </Typography>
  );
}

interface ComponentProps {
  residue: Residue;
  colors?: SelectedColorScheme;
  columnsLetterOccurrences: ColumnsLetterOccurrences;
  features: SequenceFeature[];
  onResidueHover: HandleResidueHover;
  sessionSequenceType: SequenceType;
  activeAlignment: boolean;
}

function SequenceResidueHoverTooltip({
  residue, colors, columnsLetterOccurrences, features, onResidueHover, sessionSequenceType,
  activeAlignment,
}: ComponentProps) {
  return (
    <Tooltip
      open
      title={(
        <div onMouseEnter={() => onResidueHover(undefined)}>
          <ResidueTitle
            residue={residue}
            color={colors && colors.infos[residue.letter] ? colors.infos[residue.letter].color : undefined}
            sessionSequenceType={sessionSequenceType}
          />

          {columnsLetterOccurrences[residue.position] && columnsLetterOccurrences[residue.position][residue.letter] && (
            <Occurrence occurrence={columnsLetterOccurrences[residue.position][residue.letter]} />
          )}

          {activeAlignment && (
            <Typography variant="body2">
              {`Alignment Position: ${residue.position + 1}`}
            </Typography>
          )}

          {residue.unAlignedPosition !== undefined && (
            <Typography variant="body2">
              {`Sequence Position: ${residue.unAlignedPosition + 1}`}
            </Typography>
          )}

          <Typography variant="body2">
            {`Sequence Name: ${residue.sequenceName}`}
          </Typography>

          <Typography variant="body2">
            {`Sequence ID: ${residue.sequenceQualifiedId}`}
          </Typography>

          {residue.featureDefinitions.length > 0 && (
            <>
              <Box mt={1} />

              {residue.featureDefinitions.map((definition, i) => {
                const feature = features.find((f) => (
                  buildFeatureId(f) === `${definition.type}_${definition.name}`
                ));

                return (
                   
                  <Box key={`${definition.name}-${i}`}>
                    <ColorPicker color={definition.color} />
                    <Typography display="inline" variant="body2">
                      {definition.visible ? definition.name : `${definition.name} (hidden)`}
                      {feature?.score ? ` - Score: ${feature.score}` : ''}
                    </Typography>
                    {feature?.description && (
                      <Box ml={3}>
                        <Typography display="inline" variant="caption">
                          {feature.description}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </>
          )}
        </div>
      )}
      placement="bottom-start"
      PopperProps={{
        popperOptions: {
          strategy: 'fixed',
        },
        modifiers: [
          {
            // Push the tooltip a bit further than the residue to avoid overlap
            name: 'offset',
            options: {
              offset: [12, 12],
            },
          },
        ],
      }}
      componentsProps={{
        tooltip: {
          sx: {
            // Can't handle via sx class yet, seems not to be supported yet
            backgroundColor: 'white',
            color: theme.palette.text.primary,
            boxShadow: 1,
          },
        },
        popper: {
          sx: classes.popper,
        },
      }}
    >
      <Box
        sx={{
          ...classes.tooltipTarget,
          displayPrint: 'none',
          top: residue.top,
          left: residue.left,
        }}
      />
    </Tooltip>
  );
}

export default SequenceResidueHoverTooltip;
